import React, { useState, type FormEvent } from 'react';
import {
  KeyRound,
  AlertCircle,
  Loader2,
  CheckCircle,
  FileText,
  MessageSquare,
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@shared/Button';
import Input from '@shared/Input';
import Card from '@shared/Card';
import FileUpload from '@shared/FileUpload';
import api from '@/utils/api';
import T from '@/utils/i18n.ts';
import { Link } from 'react-router-dom';

type ActivationMode = 'online' | 'offline';

const LicenseActivationPage: React.FC = () => {
  const [activationMode, setActivationMode] = useState<ActivationMode>('online');
  const [licenseKey, setLicenseKey] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileValidation, setFileValidation] = useState<{
    isValid: boolean;
    license?: any;
    error?: string;
    warnings?: string[];
  } | null>(null);

  const handleActivate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      setLoading(false);
      return;
    }

    try {
      const response = await api.activateLicense(licenseKey.trim().toUpperCase());

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to activate license');
      }

      toast.success('License activated successfully!');

      // Redirect to license after successful activation
      setTimeout(() => {
        window.location.href = '/license';
      }, 1500);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Invalid license key');
      toast.error(err.message || 'Failed to activate license');
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelected = async (files: File[]): Promise<void> => {
    const file = files[0]; // Only one file allowed
    if (!file) return;

    setSelectedFile(file);
    setError(null);
    setFileValidation(null);

    try {
      // Read file content
      const fileContent = await file.text();
      const licenseFile = JSON.parse(fileContent);

      // Validate file without activating
      const validation = await api.validateOfflineLicenseFile(licenseFile);

      setFileValidation({
        isValid: validation.isValid,
        license: validation.license,
        error: validation.error,
        warnings: validation.warnings,
      });

      if (!validation.isValid) {
        setError(validation.error || 'Invalid license file');
      }
    } catch (err: any) {
      console.error('File validation error:', err);
      setError(err.message || 'Failed to read license file');
      setFileValidation(null);
    }
  };

  const handleFileRemove = (): void => {
    setSelectedFile(null);
    setFileValidation(null);
    setError(null);
  };

  const handleOfflineActivation = async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a license file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Read file content
      const fileContent = await selectedFile.text();
      const licenseFile = JSON.parse(fileContent);

      // Send to backend for activation
      const response = await api.activateOfflineLicense(licenseFile);

      if (!response.success) {
        throw new Error(response.message || 'Failed to activate license');
      }

      toast.success('License activated successfully!');

      if (response.warnings && response.warnings.length > 0) {
        response.warnings.forEach((warning: string) => {
          toast(warning, { icon: '⚠️' });
        });
      }

      // Redirect to license after successful activation
      setTimeout(() => {
        window.location.href = '/license';
      }, 1500);
    } catch (err: any) {
      console.error('Offline activation error:', err);
      setError(err.message || 'Failed to activate license');
      toast.error(err.message || 'Failed to activate license');
    } finally {
      setLoading(false);
    }
  };

  const formatLicenseKey = (value: string): string => {
    // Remove any non-alphanumeric characters except hyphens
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    return cleaned;
  };

  return (
    <div className="flex items-center justify-center w-full h-full p-4">
      <Card className="w-full max-w-md shadow-xl">
        <div className="p-8">
          {/* Logo/Icon */}
          <Button
            variant="ghost"
            onClick={() => history.back()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" /> Back
          </Button>
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-10 h-10 text-blue-600" />
            </div>
            <Link to="/" className={`flex justify-center gap-3`}>
              <div className={`flex items-center z-60 space-x-2`}>
                <MessageSquare className={`w-8 h-8 mx-2 text-blue-600`} />
                <span
                  className={`text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent`}
                >
                  {T.product.name['en']}
                </span>
              </div>
            </Link>
            <p className="text-gray-600 mt-2">Activate your license</p>
          </div>

          {/* Mode Selector */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              type="button"
              onClick={() => {
                setActivationMode('online');
                setError(null);
              }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                activationMode === 'online'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              License Key
            </button>
            <button
              type="button"
              onClick={() => {
                setActivationMode('offline');
                setError(null);
              }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                activationMode === 'offline'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              License File
            </button>
          </div>

          {/* Online Activation Mode */}
          {activationMode === 'online' && (
            <form onSubmit={handleActivate} className="space-y-6">
              <div>
                <label
                  htmlFor="licenseKey"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  License Key
                </label>
                <Input
                  id="licenseKey"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(formatLicenseKey(e.target.value))}
                  placeholder="SDAI-XXXX-YYYY-ZZZZ"
                  className="w-full text-center font-mono text-lg tracking-wider"
                  error={!!error}
                  disabled={loading}
                />
                {error && (
                  <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !licenseKey.trim()}
                className="w-full py-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Activating...
                  </>
                ) : (
                  'Activate License'
                )}
              </Button>
            </form>
          )}

          {/* Offline Activation Mode */}
          {activationMode === 'offline' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">License File</label>
                <p className="text-sm text-gray-500 mb-3">
                  Upload the license file (.dat) provided by your vendor.
                </p>

                {/* Show FileUpload only when no file is selected */}
                {!selectedFile ? (
                  <FileUpload
                    onFilesSelected={handleFilesSelected}
                    onFileRemove={handleFileRemove}
                    supportedFormats={['dat', 'json']}
                    maxFileSize={5 * 1024 * 1024} // 5MB max for license files
                    allowMultiple={false}
                    showFileList={false}
                    disabled={loading}
                  />
                ) : (
                  /* Show selected file with validation results */
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-sm text-gray-500">
                            {(selectedFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileValidation(null);
                          setError(null);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                      >
                        Change
                      </button>
                    </div>

                    {/* File Validation Result */}
                    {fileValidation && (
                      <div
                        className={`p-3 rounded-lg border ${
                          fileValidation.isValid
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {fileValidation.isValid ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          )}
                          <span
                            className={`font-medium ${
                              fileValidation.isValid ? 'text-green-700' : 'text-red-700'
                            }`}
                          >
                            {fileValidation.isValid ? 'Valid License File' : 'Invalid License File'}
                          </span>
                        </div>

                        {fileValidation.license && (
                          <div className="mt-2 text-sm space-y-1 text-gray-700">
                            <p>
                              <span className="text-gray-500">Organization:</span>{' '}
                              {fileValidation.license.organizationName}
                            </p>
                            <p>
                              <span className="text-gray-500">License Key:</span>{' '}
                              {fileValidation.license.licenseKey}
                            </p>
                            <p>
                              <span className="text-gray-500">Expires:</span>{' '}
                              {new Date(fileValidation.license.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                        )}

                        {fileValidation.warnings && fileValidation.warnings.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {fileValidation.warnings.map((warning, idx) => (
                              <p
                                key={idx}
                                className="text-sm text-yellow-700 flex items-start gap-1"
                              >
                                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{warning}</span>
                              </p>
                            ))}
                          </div>
                        )}

                        {fileValidation.error && (
                          <p className="mt-2 text-sm text-red-600">{fileValidation.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {error && !fileValidation && (
                  <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <Button
                type="button"
                onClick={handleOfflineActivation}
                disabled={loading || !selectedFile || (fileValidation! && !fileValidation.isValid)}
                className="w-full py-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Activating...
                  </>
                ) : (
                  'Activate Offline License'
                )}
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Don't have a license key?{' '}
              <a
                href="https://smartdocs.ai/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Purchase a license
              </a>
            </p>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400 text-center">
              {activationMode === 'online'
                ? "Your license key was provided when you purchased SmartDocs AI. If you've lost your key, please contact support."
                : 'For air-gapped deployments, use the offline license file provided by your vendor.'}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LicenseActivationPage;
